/**
 * What happens when the visitor saves a cookie choice (`features.cookieConsent`).
 *
 * GRANT — start the category now, without a reload: the inline bootstrap in
 * index.html starts GA4 / the Meta Pixel (`window.uztConsentUpdated`, see
 * `consent-bootstrap.ts`), PostHog is initialised here, and the landing click
 * ids / UTMs that arrived before the choice are stored from the landing query
 * the bootstrap kept in memory.
 *
 * REFUSAL — delete what that category may have left on the device (a choice
 * that expired after 12 months and is now refused counts too; the AEPD expects
 * withdrawal to remove the cookies, guía §3.2.9).
 *
 * REVOCATION of something that was running — the same deletion, PostHog told to
 * stop first so nothing queued is flushed, then a reload: a loaded gtag.js or
 * fbevents.js cannot be unloaded from a page, and the reloaded page starts only
 * what is still granted.
 *
 * Never reached on UZ/KZ: only the banner calls it, and the banner renders only
 * where consent is required.
 */
import { CONSENT_COOKIE } from '@tenders/shared';
import { cookieDomainAttr, readConsent, writeConsent, type ConsentChoice } from './cookie-consent';
import { initPostHog, phStopForRevocation } from './posthog';
import { captureAdClickIds } from './meta-cookies';
import { trackOnce } from './funnel';
import { sessionContext } from './session-context';

declare global {
  interface Window {
    /** Set by the consent bootstrap in index.html: start what is now granted. */
    uztConsentUpdated?: () => void;
    /** The landing query string, kept in memory by the consent bootstrap. */
    uztLandingSearch?: string;
    /** The landing pathname, kept in memory by the consent bootstrap. */
    uztLandingPath?: string;
  }
}

/** Meta: the Pixel's cookies and our click-id memory. */
const MARKETING_COOKIES = ['_fbp', '_fbc'];
const MARKETING_LOCAL = ['tenders_fbclid_v2', 'tenders_gclid_v1'];
const MARKETING_SESSION = ['tenders_fbclid'];
/** PostHog (`ph_<key>_posthog`, `__ph_opt_in_out_…`) and GA4 (`_ga`, `_ga_<id>`). */
const ANALYTICS_COOKIE_PREFIXES = ['ph_', '__ph_', '_ga'];
const ANALYTICS_LOCAL_PREFIXES = ['ph_', '__ph_', 'uzt:ga-purchase:'];
/** Shared by both categories: kept while either is granted. */
const SHARED_LOCAL = ['tenders_vid', 'tenders_referrer_v2'];
const SHARED_LOCAL_PREFIXES = ['tenders_utm_'];
const SHARED_SESSION = ['tenders_referrer'];

/** Expire a cookie on every scope it may have been written with. */
function expireCookie(name: string): void {
  const scopes = new Set(['', cookieDomainAttr()]);
  if (typeof location !== 'undefined' && location.hostname) {
    scopes.add(`; domain=${location.hostname}`);
    scopes.add(`; domain=.${location.hostname}`);
  }
  for (const scope of scopes) {
    try {
      document.cookie = `${name}=; path=/; max-age=0${scope}`;
    } catch {
      /* cookies unavailable */
    }
  }
}

function cookieNames(): string[] {
  try {
    return document.cookie
      .split(';')
      .map((c) => c.split('=')[0]?.trim() ?? '')
      .filter((n) => n && n !== CONSENT_COOKIE);
  } catch {
    return [];
  }
}

function removeKeys(storage: Storage | undefined, exact: string[], prefixes: string[]): void {
  if (!storage) return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const k = storage.key(i);
      if (k && (exact.includes(k) || prefixes.some((p) => k.startsWith(p)))) keys.push(k);
    }
    for (const k of keys) storage.removeItem(k);
  } catch {
    /* storage unavailable (private mode) — nothing was stored there either */
  }
}

function safeStorage(kind: 'localStorage' | 'sessionStorage'): Storage | undefined {
  try {
    return window[kind];
  } catch {
    return undefined;
  }
}

/** Delete what a REFUSED category may have left on the device. Exported for tests. */
export function clearRefused(choice: ConsentChoice): void {
  if (typeof document === 'undefined') return;
  const local = safeStorage('localStorage');
  const session = safeStorage('sessionStorage');
  if (!choice.analytics) {
    for (const n of cookieNames()) {
      if (ANALYTICS_COOKIE_PREFIXES.some((p) => n.startsWith(p))) expireCookie(n);
    }
    removeKeys(local, [], ANALYTICS_LOCAL_PREFIXES);
    removeKeys(session, [], ANALYTICS_LOCAL_PREFIXES);
  }
  if (!choice.marketing) {
    for (const n of MARKETING_COOKIES) expireCookie(n);
    removeKeys(local, MARKETING_LOCAL, []);
    removeKeys(session, MARKETING_SESSION, []);
  }
  if (!choice.analytics && !choice.marketing) {
    removeKeys(local, SHARED_LOCAL, SHARED_LOCAL_PREFIXES);
    removeKeys(session, SHARED_SESSION, []);
  }
}

export interface ApplyConsentDeps {
  /** Reload the page after a revocation. Injectable for tests. */
  reload: () => void;
}

const defaultDeps: ApplyConsentDeps = {
  reload: () => window.location.reload(),
};

/** Save the visitor's choice and act on it — see the module comment. */
export async function applyConsentChoice(
  choice: ConsentChoice,
  deps: ApplyConsentDeps = defaultDeps,
): Promise<void> {
  const before = readConsent();
  const revokedAnalytics = before?.analytics === true && !choice.analytics;
  const revokedMarketing = before?.marketing === true && !choice.marketing;

  writeConsent(choice);

  if (revokedAnalytics) await phStopForRevocation();
  clearRefused(choice);
  if (revokedAnalytics || revokedMarketing) {
    deps.reload();
    return;
  }

  try {
    window.uztConsentUpdated?.();
  } catch {
    /* the bootstrap must never break the banner */
  }
  if (choice.analytics || choice.marketing) captureAdClickIds(window.uztLandingSearch);
  if (choice.analytics) {
    // The visitor has usually moved on from the landing page by the time they
    // answer the banner, and the ad's `?utm_*` went with it. PostHog and the
    // `session_start` row both take the campaign and the landing from the
    // address the visit ARRIVED on, kept in memory by the bootstrap (review
    // 21.10, А3: a Meta-ad signup booked as «google organic»).
    initPostHog({ landingSearch: window.uztLandingSearch, landingPath: window.uztLandingPath });
    // The shell's `session_start` ran before the choice and was refused; this
    // session's top-of-funnel row is written now instead (once per session).
    trackOnce(
      'session_start',
      sessionContext({ search: window.uztLandingSearch, pathname: window.uztLandingPath }),
    );
  }
}
